import React, { useEffect, useMemo, useState } from "react";
import "./LoginPage.scss";
import Input from "../../atoms/Input/Input";
import { useNavigate } from "react-router-dom";
import { image } from "../../../app/utils/common/image";
import { getClientSource, validateEmail } from "../../../app/utils/common/Functions";
import { useDispatch, useSelector } from "react-redux";
import ErrorText from "../../atoms/Error/errorText";
import { logIn } from "../../../Redux/Apis/authApi";
import {
  CalendarOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ProjectOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const BRAND_COPY = {
  tcs: {
    title: "Company Dashboard",
    subtitle:
      "Sign in to manage attendance, projects, payroll, and everyday company operations.",
    features: [
      { icon: TeamOutlined, label: "Attendance", tone: "violet" },
      { icon: ProjectOutlined, label: "Projects", tone: "blue" },
      { icon: CalendarOutlined, label: "Leave & HR", tone: "teal" },
    ],
  },
  sattva: {
    title: "Sattva Dashboard",
    subtitle:
      "Sign in to collaborate with your team, track work, and stay aligned across the company.",
    features: [
      { icon: TeamOutlined, label: "Attendance", tone: "violet" },
      { icon: ProjectOutlined, label: "Projects", tone: "blue" },
      { icon: CalendarOutlined, label: "Operations", tone: "teal" },
    ],
  },
};

const LoginPage = () => {
  const [user, setUser] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();
  const { isAuth, loading } = useSelector((state) => state.auth.auth);
  const navigate = useNavigate();

  const brand = useMemo(
    () => BRAND_COPY[getClientSource()] || BRAND_COPY.tcs,
    []
  );

  useEffect(() => {
    if (isAuth) navigate("/dashboard");
  }, [isAuth, navigate]);

  const validateData = () => {
    if (
      user.email.trim().length &&
      user.password.trim().length &&
      validateEmail(user.email.trim())
    ) {
      return true;
    }
    return false;
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    if (validateData()) {
      dispatch(logIn({ email: user.email, password: user.password }));
      return;
    }
    setShowError(true);
  };

  return (
    <div className="login-form">
      <div className="login-shell">
        <div className="login-frame">
          <aside className="login-brand-panel" aria-hidden="true">
            <div className="login-brand-panel__content">
              <img
                src={image.LOGO}
                alt=""
                className="login-brand-panel__logo"
              />
              <h1 className="login-brand-panel__title">{brand.title}</h1>
              <p className="login-brand-panel__subtitle">{brand.subtitle}</p>

              <div className="login-brand-panel__chips">
                {brand.features.map(({ icon: Icon, label, tone }) => (
                  <div
                    key={label}
                    className={`login-brand-chip login-brand-chip--${tone}`}
                  >
                    <Icon />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="login-brand-panel__visual" aria-hidden="true">
              <div className="login-visual-card login-visual-card--back" />
              <div className="login-visual-card login-visual-card--main">
                <div className="login-visual-card__top">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="login-visual-card__sidebar" />
                <div className="login-visual-card__body">
                  <div className="login-visual-card__row login-visual-card__row--wide" />
                  <div className="login-visual-card__grid">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="login-visual-card__row" />
                  <div className="login-visual-card__row login-visual-card__row--short" />
                </div>
              </div>
            </div>
          </aside>

          <main className="login-main-panel">
            <div className="login-card">
              <div className="login-card__header">
                <img
                  src={image.LOGO}
                  alt="Company logo"
                  className="login-card__logo-mobile"
                />
                <h2 className="login-card__title">Welcome back</h2>
                <p className="login-card__subtitle">
                  Enter your company credentials to continue.
                </p>
              </div>

              <form className="login-card__form" onSubmit={handleSubmit} noValidate>
                <div className="login-card__field">
                  <Input
                    label="Email address"
                    placeholder="name@company.com"
                    type="email"
                    id="email"
                    value={user.email}
                    onChange={(e) =>
                      setUser({ ...user, email: e.target.value ?? "" })
                    }
                  />
                  <ErrorText
                    message="Please provide a valid email address."
                    show={
                      showError &&
                      (user.email.trim().length === 0 ||
                        !validateEmail(user.email.trim()))
                    }
                  />
                </div>

                <div className="login-card__field">
                  <div className="login-card__password-wrap">
                    <Input
                      label="Password"
                      placeholder="Enter your password"
                      type={showPassword ? "text" : "password"}
                      id="password"
                      className="w-full"
                      classNameInput="login-card__password-input"
                      value={user.password}
                      onChange={(e) =>
                        setUser({ ...user, password: e.target.value ?? "" })
                      }
                    />
                    <button
                      type="button"
                      className="login-card__password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeInvisibleOutlined />
                      ) : (
                        <EyeOutlined />
                      )}
                    </button>
                  </div>
                  <ErrorText
                    message="Please provide your password."
                    show={showError && user.password.trim().length === 0}
                  />
                </div>

                <button
                  type="submit"
                  className="login-card__submit"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
              </form>
            </div>

            <p className="login-main-panel__footer">
              &copy; {new Date().getFullYear()} {brand.title}. All rights reserved.
            </p>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
